/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { EventType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

import {appInsights} from "../ApplicationInsightsService";

// Azure B2C Custom Policy (see policies folder) i.e B2C_1A_POLARIS_SIGNIN

const POLICY = import.meta.env.VITE_APP_AZURE_B2C_POLICY;

const AUTHORITY = import.meta.env.VITE_APP_AZURE_B2C_AUTHORITY;

const AUTHORITY_DOMAIN = import.meta.env.VITE_APP_AZURE_B2C_AUTHORITY_DOMAIN;
// Azure B2C Client i.e JohnFTest (Sandbox)

const CLIENTID = import.meta.env.VITE_APP_AZURE_B2C_CLIENTID;

const REDIRECTURI = import.meta.env.VITE_APP_AZURE_B2C_REDIRECTURI;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: POLICY
    },
    authorities: {
        signUpSignIn: {
            authority: AUTHORITY
        }
    },
    authorityDomain: AUTHORITY_DOMAIN
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
        clientId: CLIENTID, // Sandbox/Test/UAT
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: REDIRECTURI, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            logLevel: LogLevel.Error,
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        appInsights.trackException({ error: new Error(message), severityLevel: SeverityLevel.Error });
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        appInsights.trackException({ error: new Error(message), severityLevel: SeverityLevel.Information });
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        appInsights.trackException({ error: new Error(message), severityLevel: SeverityLevel.Verbose });
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        appInsights.trackException({ error: new Error(message), severityLevel: SeverityLevel.Warning });
                        return;
                    default:
                        return;
                }
            }
        }
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: []
};

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

msalInstance.addEventCallback((event) => {
    if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        event.payload.account
    ) {
        msalInstance.setActiveAccount(event.payload.account);
    }
});
